import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/components/feature/Footer';
import ProductGallery from './components/ProductGallery';
import ProductInfo from './components/ProductInfo';
import RelatedProducts from './components/RelatedProducts';
import SeoHead from '@/components/feature/SeoHead';
import { useProduct } from '@/hooks/useProducts';
import { useProductTranslation } from '@/hooks/useProductTranslation';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { product, loading, error } = useProduct(id ?? '');
  const { getProductName, getProductDescription, getProductLongDescription, getProductSpecs, getProductHighlights, getProductFaqs } = useProductTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const productJsonLd = useMemo(() => {
    if (!product) return null;
    const homeLabel = t('products_breadcrumb_home', '首页');
    const productsLabel = t('products_breadcrumb', '产品中心');
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description,
        image: product.images ?? [product.image],
        brand: {
          '@type': 'Brand',
          name: 'Koorpet',
        },
        offers: {
          '@type': 'Offer',
          priceCurrency: 'USD',
          price: product.price,
          availability: 'https://schema.org/InStock',
          url: `${siteUrl}/products/${product.id}`,
        },
        category: product.category,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: homeLabel, item: siteUrl },
          { '@type': 'ListItem', position: 2, name: productsLabel, item: `${siteUrl}/products` },
          { '@type': 'ListItem', position: 3, name: product.name, item: `${siteUrl}/products/${product.id}` },
        ],
      },
    ];
  }, [product, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf8f5]">
        <Navbar />
        <div className="pt-24 pb-16 max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 animate-pulse">
            <div className="h-[480px] bg-[#f5f0eb] rounded-2xl"></div>
            <div className="flex flex-col gap-4 pt-4">
              <div className="h-4 w-20 bg-[#f0ebe4] rounded-full"></div>
              <div className="h-8 w-3/4 bg-[#f0ebe4] rounded"></div>
              <div className="h-4 w-full bg-[#f0ebe4] rounded"></div>
              <div className="h-4 w-2/3 bg-[#f0ebe4] rounded"></div>
              <div className="h-12 w-full bg-[#f0ebe4] rounded-xl mt-4"></div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-16 bg-[#f0ebe4] rounded-xl"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center gap-4 pt-20">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#f5f0eb]">
            <i className="ri-search-line text-2xl text-[#C8714A]"></i>
          </div>
          <p className="text-[#888] text-base">{error ?? t('detail_not_found', '找不到该产品')}</p>
          <button
            onClick={() => navigate('/products')}
            className="px-6 py-2.5 rounded-full bg-[#C8714A] text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            {t('detail_back_to_products', '返回产品列表')}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const displayName = getProductName(product);

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <SeoHead
        title={`${product.name} - Koorpet | ${product.category}`}
        description={product.description}
        keywords={`${product.name},${product.category},猫抓板,宠物用品,Koorpet`}
        canonical={`/products/${product.id}`}
        ogImage={product.images?.[0] ?? product.image}
        ogType="product"
        jsonLd={productJsonLd ?? undefined}
      />
      <Navbar />

      <div className="pt-20 pb-0 px-6 md:px-10 max-w-7xl mx-auto">
        <div className="flex items-center gap-2 text-xs text-[#aaa] py-4">
          <Link to="/" className="hover:text-[#C8714A] transition-colors cursor-pointer">{t('products_breadcrumb_home')}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/products" className="hover:text-[#C8714A] transition-colors cursor-pointer">{t('products_breadcrumb')}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-[#666]">{displayName}</span>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div className="lg:sticky lg:top-24 lg:self-start">
            <ProductGallery images={product.images ?? [product.image]} name={displayName} />
          </div>
          <div>
            <ProductInfo
              name={displayName}
              productCode={product.product_code}
              category={product.category}
              price={product.price}
              originalPrice={product.original_price}
              tags={product.tags ?? []}
              description={getProductDescription(product)}
              longDescription={getProductLongDescription(product)}
              specs={getProductSpecs(product)}
              highlights={getProductHighlights(product)}
              faqs={getProductFaqs(product)}
            />
          </div>
        </div>

        <RelatedProducts currentId={product.id} category={product.category} />
      </main>

      <Footer />
    </div>
  );
}